import type { createStore } from "jotai";
import { atomWithStorage } from "jotai/utils";

type JotaiStore = ReturnType<typeof createStore>;

/**
 * Valid contexts for draft messages.
 * Add new contexts here as the feature expands.
 */
export type DraftContext = "chat" | "clinicalNote" | "orders";

/**
 * Data structure for a single draft message
 */
export interface DraftMessageData {
  text: string;
  timestamp: number;
}

/**
 * Map of draft messages keyed by context::patientId::encounterId
 * Example keys:
 * - "chat::patient123-uuid::encounter456-uuid"
 * - "clinicalNote::patient123-uuid::encounter456-uuid"
 * - "orders::patient123-uuid::no-encounter"
 */
export interface DraftMessages {
  [key: string]: DraftMessageData;
}

const DRAFT_STORAGE_KEY = "draft_messages";
const DRAFT_EXPIRY_DAYS = 7;

/**
 * Unified atom for storing all draft messages across the application
 * Persists to localStorage for durability across page refreshes
 */
export const draftMessagesAtom = atomWithStorage<DraftMessages>(
  DRAFT_STORAGE_KEY,
  {},
);

/**
 * Generates a consistent key for storing/retrieving draft messages
 * Uses :: as separator to avoid confusion with hyphens in UUIDs
 * @param context - The context type (e.g., 'chat', 'clinicalNote', 'orders')
 * @param patientId - The patient ID (UUID)
 * @param encounterId - Optional encounter ID (UUID)
 * @returns Formatted key string (e.g., "chat::uuid1::uuid2")
 */
export function getDraftKey(
  context: DraftContext,
  patientId: string,
  encounterId?: string,
): string {
  const encounterPart = encounterId || "no-encounter";
  return `${context}::${patientId}::${encounterPart}`;
}

/**
 * Removes draft messages older than the configured expiry period
 * @param messages - Current draft messages
 * @returns Cleaned draft messages with stale entries removed
 */
export function cleanupStaleDrafts(messages: DraftMessages): DraftMessages {
  const now = Date.now();
  const expiryMs = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

  const cleaned: DraftMessages = {};
  for (const [key, value] of Object.entries(messages)) {
    if (now - value.timestamp < expiryMs) {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

/**
 * Initialize draft message cleanup. Call once at app startup.
 * Removes stale drafts from localStorage to prevent unbounded growth.
 *
 * @param store - The Jotai store instance
 */
export function initDraftCleanup(store: JotaiStore): void {
  const currentDrafts = store.get(draftMessagesAtom);
  const cleanedDrafts = cleanupStaleDrafts(currentDrafts);

  // Only update if something was actually cleaned
  if (Object.keys(cleanedDrafts).length !== Object.keys(currentDrafts).length) {
    store.set(draftMessagesAtom, cleanedDrafts);
  }
}
